import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  TextField,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MyButton } from '../index';
import { paymentModalStyles } from './PaymentModal.styles';

const PaymentModal = ({
  open = false,
  onClose,
  onConfirm,
  expenseRequest = null,
  loading = false,
  ...props
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [paidAmount, setPaidAmount] = useState('');

  useEffect(() => {
    if (expenseRequest && open) {
      // If expense already has a paid_amount, pre-fill it
      setPaidAmount(expenseRequest.paid_amount || '');
    } else if (!open) {
      setPaidAmount('');
    }
  }, [expenseRequest, open]);

  const handleConfirm = async () => {
    if (onConfirm && paidAmount && parseFloat(paidAmount) >= 0) {
      await onConfirm(parseFloat(paidAmount));
      setPaidAmount('');
    }
  };

  const handleClose = () => {
    if (!loading) {
      setPaidAmount('');
      onClose();
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow empty string, numbers, and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setPaidAmount(value);
    }
  };

  const isValid = paidAmount !== '' && parseFloat(paidAmount) >= 0;

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose(e, reason);
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      sx={paymentModalStyles.dialog}
      {...props}
    >
      <DialogTitle sx={paymentModalStyles.dialogTitle}>
        <Box sx={paymentModalStyles.titleContainer}>
          <Box sx={paymentModalStyles.titleContent}>
            <PaymentIcon sx={paymentModalStyles.paymentIcon} />
            <Typography variant="h6" sx={paymentModalStyles.titleText}>
              {t('expenses.modals.payment.title')}
            </Typography>
          </Box>

          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={paymentModalStyles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={paymentModalStyles.dialogContent}>
        <Typography variant="body1" sx={paymentModalStyles.messageText}>
          {t('expenses.modals.payment.message')}
        </Typography>
        {expenseRequest && (
          <Box sx={paymentModalStyles.expenseInfoContainer}>
            <Typography variant="body2" sx={paymentModalStyles.expenseInfoLabel}>
              {t('expenses.modals.payment.expenseAmount')}:
            </Typography>
            <Typography variant="body1" sx={paymentModalStyles.expenseInfoValue}>
              {expenseRequest.amount ? `${expenseRequest.amount} ₺` : 'N/A'}
            </Typography>
            {expenseRequest.paid_amount && (
              <>
                <Typography variant="body2" sx={paymentModalStyles.expenseInfoLabel}>
                  {t('expenses.modals.payment.previouslyPaid')}:
                </Typography>
                <Typography variant="body1" sx={paymentModalStyles.expenseInfoValue}>
                  {expenseRequest.paid_amount} ₺
                </Typography>
              </>
            )}
          </Box>
        )}
        <TextField
          fullWidth
          type="text"
          variant="outlined"
          label={t('expenses.modals.payment.paidAmountLabel')}
          placeholder={t('expenses.modals.payment.paidAmountPlaceholder')}
          value={paidAmount}
          onChange={handleAmountChange}
          sx={paymentModalStyles.textField}
          disabled={loading}
          inputProps={{
            inputMode: 'decimal',
            pattern: '[0-9]*\\.?[0-9]*',
          }}
          helperText={t('expenses.modals.payment.helperText')}
        />
      </DialogContent>
      <DialogActions sx={paymentModalStyles.dialogActions}>
        <MyButton
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
          sx={paymentModalStyles.cancelButton}
        >
          {t('modals.delete.cancel')}
        </MyButton>

        <MyButton
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={loading || !isValid}
          sx={paymentModalStyles.confirmButton}
        >
          {loading ? t('expenses.modals.payment.saving') : t('expenses.modals.payment.confirm')}
        </MyButton>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentModal;

